import React, { useState } from 'react';
import { useAuth } from '../../../context/AuthContext';
import { Note } from '../../../types/camper';
import { addNote, updateNote, deleteNote } from '../../../utils/camperManagement';
import './NotesSection.css';

interface NotesSectionProps {
  camperId: string;
  notes: Note[];
  onNotesChange: () => void;
  isReadOnly?: boolean;
}

const NotesSection: React.FC<NotesSectionProps> = ({ camperId, notes, onNotesChange, isReadOnly = false }) => {
  const { currentUser } = useAuth();
  const [newNoteContent, setNewNoteContent] = useState('');
  const [isPersistent, setIsPersistent] = useState(false);
  const [editingNoteId, setEditingNoteId] = useState<string | null>(null);
  const [editContent, setEditContent] = useState('');
  const [editIsPersistent, setEditIsPersistent] = useState(false);
  const [error, setError] = useState('');

  const handleAddNote = async () => {
    if (!newNoteContent.trim() || !currentUser) return;

    try {
      await addNote(camperId, newNoteContent, currentUser.uid, isPersistent);
      setNewNoteContent('');
      setIsPersistent(false);
      onNotesChange();
    } catch (err) {
      setError('Failed to add note');
      console.error(err);
    }
  };

  const handleUpdateNote = async (noteId: string) => {
    if (!editContent.trim()) return;

    try {
      await updateNote(camperId, noteId, editContent, editIsPersistent);
      setEditingNoteId(null);
      setEditContent('');
      setEditIsPersistent(false);
      onNotesChange();
    } catch (err) {
      setError('Failed to update note');
      console.error(err);
    }
  };

  const handleDeleteNote = async (noteId: string) => {
    if (!window.confirm('Are you sure you want to delete this note?')) return;

    try {
      await deleteNote(camperId, noteId);
      onNotesChange();
    } catch (err) {
      setError('Failed to delete note');
      console.error(err);
    }
  };

  const startEditing = (note: Note) => {
    setEditingNoteId(note.id);
    setEditContent(note.content);
    setEditIsPersistent(note.isPersistent);
  };

  const cancelEditing = () => {
    setEditingNoteId(null);
    setEditContent('');
    setEditIsPersistent(false);
  };

  const formatDate = (date: Date) => {
    return new Date(date).toLocaleString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: 'numeric',
      minute: 'numeric'
    });
  };

  return (
    <div className="notes-section">
      <h2>Notes</h2>
      {error && <div className="error-message">{error}</div>}

      {!isReadOnly && (
        <div className="add-note">
          <textarea
            value={newNoteContent}
            onChange={(e) => setNewNoteContent(e.target.value)}
            placeholder="Add a new note..."
            rows={3}
          />
          <div className="note-options">
            <label className="persistence-option">
              <input
                type="checkbox"
                checked={isPersistent}
                onChange={(e) => setIsPersistent(e.target.checked)}
              />
              <span>Persistent Note (stays across years)</span>
            </label>
            <button 
              onClick={handleAddNote}
              disabled={!newNoteContent.trim()}
              className="add-note-button"
            >
              Add Note
            </button>
          </div>
        </div>
      )}

      <div className="notes-list">
        {notes.length === 0 ? (
          <div className="no-notes">No notes yet</div>
        ) : (
          notes.map(note => (
            <div key={note.id} className={`note-item ${note.isPersistent ? 'persistent' : ''}`}>
              {editingNoteId === note.id ? (
                <div className="note-edit">
                  <textarea
                    value={editContent}
                    onChange={(e) => setEditContent(e.target.value)}
                    rows={3}
                  />
                  <div className="note-options">
                    <label className="persistence-option">
                      <input
                        type="checkbox"
                        checked={editIsPersistent}
                        onChange={(e) => setEditIsPersistent(e.target.checked)}
                      />
                      <span>Persistent Note</span>
                    </label>
                    <div className="note-actions">
                      <button 
                        onClick={() => handleUpdateNote(note.id)}
                        disabled={!editContent.trim()}
                      >
                        Save
                      </button>
                      <button onClick={cancelEditing}>Cancel</button>
                    </div>
                  </div>
                </div>
              ) : (
                <>
                  <div className="note-content">{note.content}</div>
                  <div className="note-meta">
                    <span className="note-date">
                      {formatDate(note.createdDate)}
                      {note.lastModifiedDate > note.createdDate && 
                        ` (edited ${formatDate(note.lastModifiedDate)})`
                      }
                    </span>
                    {note.isPersistent && (
                      <span className="persistence-badge">Persistent</span>
                    )}
                  </div>
                  {!isReadOnly && (
                    <div className="note-actions">
                      <button onClick={() => startEditing(note)}>Edit</button>
                      <button onClick={() => handleDeleteNote(note.id)}>Delete</button>
                    </div>
                  )}
                </>
              )}
            </div>
          ))
        )}
      </div>
    </div>
  );
};

export default NotesSection; 